import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';
import { useFormContext } from 'react-hook-form';
import useUser from 'src/hooks/use-user';

import { Typography } from 'src/typography';
import { DEFAULT_INPUT_MAX_LENGTH } from 'shared/constants';
import { RadioGroup, CheckboxOld } from 'shared/components';
import { isRecMed } from 'shared/core/helpers/dispensaries';
import _FormFormattedInput from 'components/form-inputs/form-formatted-input';

import useCart from 'hooks/use-cart';
import useSwitchMenuType from 'src/hooks/use-switch-menu-type';

import DirectOrders from './direct-orders';
import { FormTextInput as _FormTextInput } from './form-input';
import { InputContainer as _InputContainer, InputLabel } from './components';
import { ConsumerOrderInformation } from './consumer-order-information';

export default function OrderInformation(props) {
  const { dispensary, isAnonymousCheckout, setIsAnonymousCheckout } = props;
  const emailRequired = dispensary.storeSettings?.requireEmailAddressForGuestCheckout;
  const phoneIsRequired = dispensary?.orderTypesConfigV2.kiosk?.phoneRequired;
  const fullNameOnly = dispensary?.orderTypesConfigV2.kiosk?.fullNameOnly;
  const shouldShowBirthdateField = dispensary?.orderTypesConfigV2.kiosk?.showBirthdateField;
  const shouldShowNotesField = dispensary?.orderTypesConfigV2.kiosk?.notesField;
  const hidePhoneField = dispensary?.orderTypesConfigV2.kiosk?.hidePhoneField;
  const hideEmailField = dispensary?.orderTypesConfigV2.kiosk?.hideEmailField;
  const cart = useCart();
  const User = useUser();
  const isUserLoggedIn = useObserver(() => User.isLoggedIn);

  const orderType = useObserver(() => (cart.isMedical ? 'med' : 'rec'));
  const switchMenuType = useSwitchMenuType();
  const useAnonymousKioskCheckout = dispensary?.orderTypesConfigV2.kiosk?.anonymousCheckout && orderType === 'rec';
  const { watch } = useFormContext();
  const phoneValue = watch('phone');
  const birthValue = watch('birth');

  const [shrinkPhoneLabel, setShrinkPhoneLabel] = useState(false);
  const [shrinkBirthLabel, setShrinkBirthLabel] = useState(false);
  useEffect(() => {
    if (orderType === 'med') {
      setIsAnonymousCheckout(false);
    }
  }, [orderType, setIsAnonymousCheckout]);

  if (isUserLoggedIn) {
    return (
      <ConsumerOrderInformation isRecMed={isRecMed(dispensary)} orderType={orderType} switchMenuType={switchMenuType} />
    );
  }

  return (
    <>
      <HeaderLine>
        <Heading tag='h2' size='large'>
          Customer Information
        </Heading>
      </HeaderLine>
      {useAnonymousKioskCheckout && (
        <AnonymousCheckoutContainer isAnonymousCheckout={isAnonymousCheckout}>
          <CheckboxOld
            id='anonymous-kiosk-checkout'
            name='anonymous'
            checked={isAnonymousCheckout}
            onChange={(e) => {
              setIsAnonymousCheckout(e.target.checked);
            }}
            label='Anonymous checkout'
          />
        </AnonymousCheckoutContainer>
      )}
      {!isAnonymousCheckout && (
        <InputContainer>
          <NameBox>
            <FormTextInput
              required={!isAnonymousCheckout}
              name='firstName'
              label={fullNameOnly ? 'Full Name *' : 'First Name *'}
              inputProps={{ maxLength: DEFAULT_INPUT_MAX_LENGTH }}
            />
            {!fullNameOnly && (
              <FormTextInput
                required={!isAnonymousCheckout}
                label='Last Name *'
                name='lastName'
                inputProps={{ maxLength: DEFAULT_INPUT_MAX_LENGTH }}
              />
            )}
          </NameBox>
        </InputContainer>
      )}
      {!hidePhoneField && !isAnonymousCheckout && (
        <InputContainer>
          <FormFormattedInput
            format='phone'
            name='phone'
            label={phoneIsRequired ? 'Phone *' : 'Phone'}
            variant='filled'
            required={phoneIsRequired && !isAnonymousCheckout}
            InputLabelProps={{ shrink: shrinkPhoneLabel }}
            onFocus={() => setShrinkPhoneLabel(true)}
            onBlur={() => setShrinkPhoneLabel(!!phoneValue)}
          />
        </InputContainer>
      )}
      {!hideEmailField && !isAnonymousCheckout && (
        <InputContainer>
          <FormTextInput
            label={emailRequired ? 'Email address *' : 'Email address'}
            name='email'
            inputProps={{ maxLength: 300 }}
            required={emailRequired}
          />
        </InputContainer>
      )}
      {shouldShowBirthdateField && !isAnonymousCheckout && (
        <InputContainer>
          <FormFormattedInput
            format='date'
            name='birth'
            label='Birthday *'
            variant='filled'
            required={!isAnonymousCheckout}
            InputLabelProps={{ shrink: shrinkBirthLabel }}
            onFocus={() => setShrinkBirthLabel(true)}
            onBlur={() => setShrinkBirthLabel(!!birthValue)}
          />
        </InputContainer>
      )}
      {shouldShowNotesField && (
        <InputContainer>
          <FormTextInput name='notes' label='Notes' id='specialInstructions' inputProps={{ maxLength: 500 }} />
        </InputContainer>
      )}
      {isRecMed(dispensary) && (
        <InputContainer>
          <InputLabel width='100px'>Order Type *:</InputLabel>

          <RadioGroup
            options={[
              {
                label: 'Recreational',
                value: 'rec',
              },
              {
                label: 'Medical',
                value: 'med',
              },
            ]}
            display='inline-block'
            defaultValue={orderType}
            onChange={switchMenuType}
          />
        </InputContainer>
      )}
      <DirectOrders dispensary={dispensary} />
    </>
  );
}

const InputContainer = styled(_InputContainer)`
  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: 64px;
    margin-bottom: 16px;
  }
`;

const FormTextInput = styled(_FormTextInput)`
  height: 64px;
  & > input {
    font-size: 20px;
  }

  & + label {
    top: 4px !important;
  }
`;

const FormFormattedInput = styled(_FormFormattedInput)`
  height: 64px;
  & > input {
    font-size: 20px;
  }

  & + label {
    top: 4px !important;
  }
`;

const HeaderLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  & + div {
    margin-top: 0px;
  }
`;

const Heading = styled(Typography.Heading)`
  margin-bottom: ${({ theme }) => theme.spaces[10]};
`;

const NameBox = styled.div`
  display: flex;
  width: 100%;

  && > * {
    margin-right: 15px;

    &:last-child {
      margin-right: 0px;
    }
  }
`;

const AnonymousCheckoutContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 15px;
  margin-bottom: ${({ theme }) => theme.spaces[4]};
  height: 30px;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    flex-direction: row;
  }
`;
